import { useEffect, useState, useSyncExternalStore } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Send } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { digitoVerificadorCorrecto, formatearRNC, limpiarRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { ConfirmacionDestructiva } from '@/ui/ConfirmacionDestructiva'
import { Esqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { PanelLateral } from '@/ui/PanelLateral'
import { sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import {
  useAccionVinculo,
  useAsignar,
  useEquipoDespacho,
  useInvitarCliente,
  useVinculosDespacho,
} from '@/dominio/despacho/useDespacho'
import { PRESENTACION_VINCULO, type MiembroDespacho, type VinculoDespacho } from '@/dominio/despacho/tipos'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDelDespacho, ErrorEnLinea } from './-comun'

/**
 * Los clientes del despacho (`docs/adr/0021` §2): invitar por RNC, responder a
 * los clientes que invitan, decidir qué auxiliar ve a cada uno y dejar a un
 * cliente.
 *
 * **Invitar no dice si el RNC está en la plataforma**, y la pantalla tampoco:
 * la invitación queda «pendiente» igual exista o no, porque quién factura con
 * quién es el dato más sensible que guarda la plataforma. De un cliente que
 * rechaza se sabe el RNC que se tecleó y nada más.
 */
export const Route = createFileRoute('/despacho/clientes')({
  component: PantallaClientesDelDespacho,
})

function PantallaClientesDelDespacho() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const esTitular = sesion?.despacho?.rol === 'titular'
  const vinculos = useVinculosDespacho()
  const equipo = useEquipoDespacho()
  const accion = useAccionVinculo()
  const [asignando, setAsignando] = useState<VinculoDespacho | null>(null)
  const [renunciando, setRenunciando] = useState<VinculoDespacho | null>(null)

  if (sesion?.despacho === null || sesion === null) return null

  const todos = vinculos.data ?? []
  const deClientes = todos.filter((v) => v.estado === 'pendiente' && v.iniciadoPor === 'cliente')
  const enviadas = todos.filter((v) => v.estado === 'pendiente' && v.iniciadoPor === 'despacho')
  const vinculados = todos.filter((v) => v.estado === 'aceptado')
  const historial = todos.filter((v) => v.estado !== 'pendiente' && v.estado !== 'aceptado')
  const auxiliares = (equipo.data ?? []).filter((m) => m.rol === 'auxiliar' && m.retiradoEl === null)

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Clientes"
        descripcion={
          esTitular
            ? 'Invite a un cliente por su RNC: su administrador acepta en su portal. Cada cliente puede revocar el vínculo cuando quiera, y con eso nadie del despacho vuelve a entrar.'
            : 'Los clientes que un titular le asignó.'
        }
      />

      {esTitular ? <Invitar /> : null}

      {accion.isError ? <ErrorEnLinea error={accion.error} accion="hacer el cambio" /> : null}

      {vinculos.isError ? (
        <ErrorDelDespacho error={vinculos.error} queCargaba="los clientes" onReintentar={() => void vinculos.refetch()} />
      ) : vinculos.isLoading ? (
        <Esqueleto className="h-48 w-full" />
      ) : (
        <>
          {deClientes.length > 0 ? (
            <Tarjeta titulo="Clientes que lo invitan">
              <ul className="flex flex-col divide-y divide-gris-200">
                {deClientes.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <Cliente vinculo={v} />
                    {esTitular ? (
                      <span className="flex gap-2">
                        <Boton
                          variante="primario"
                          tamano="menor"
                          disabled={accion.isPending}
                          onClick={() => accion.mutate({ vinculo: v.id, accion: 'aceptar' })}
                        >
                          Aceptar
                        </Boton>
                        <Boton
                          tamano="menor"
                          disabled={accion.isPending}
                          onClick={() => accion.mutate({ vinculo: v.id, accion: 'rechazar' })}
                        >
                          Rechazar
                        </Boton>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}

          <Tarjeta titulo={`Vinculados (${vinculados.length})`}>
            {vinculados.length === 0 ? (
              <EstadoVacio variante="primera-vez" mensaje="Ningún cliente vinculado todavía." />
            ) : (
              <ul className="flex flex-col divide-y divide-gris-200">
                {vinculados.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <span className="flex min-w-0 flex-col">
                      <Cliente vinculo={v} />
                      {esTitular ? (
                        <span className="text-etiqueta text-gris-600">
                          {nombresAsignados(v, equipo.data ?? []) || 'Solo los titulares lo ven'}
                        </span>
                      ) : null}
                    </span>
                    {esTitular ? (
                      <span className="flex gap-2">
                        <Boton tamano="menor" onClick={() => setAsignando(v)} disabled={auxiliares.length === 0}>
                          Quién lo ve
                        </Boton>
                        <Boton tamano="menor" variante="discreto" onClick={() => setRenunciando(v)}>
                          Dejar al cliente
                        </Boton>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          {esTitular && enviadas.length > 0 ? (
            <Tarjeta titulo="Invitaciones enviadas">
              <ul className="flex flex-col divide-y divide-gris-200">
                {enviadas.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <span className="flex flex-col">
                      <span className="numero text-gris-900">RNC {formatearRNC(v.rncCliente)}</span>
                      <span className="text-etiqueta text-gris-600">Enviada el {formatearFecha(v.propuestoEl)}</span>
                    </span>
                    <Boton
                      tamano="menor"
                      variante="discreto"
                      disabled={accion.isPending}
                      onClick={() => accion.mutate({ vinculo: v.id, accion: 'retirar' })}
                    >
                      Retirar
                    </Boton>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}

          {esTitular && historial.length > 0 ? (
            <Tarjeta titulo="Historial">
              <ul className="flex flex-col divide-y divide-gris-200">
                {historial.map((v) => (
                  <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <Cliente vinculo={v} />
                    <span className="text-etiqueta text-gris-600">
                      {formatearFecha(v.terminadoEl ?? v.respondidoEl ?? v.propuestoEl)}
                      {v.terminadoLado ? ` · por ${v.terminadoLado === 'cliente' ? 'el cliente' : 'el despacho'}` : ''}
                      {v.motivoFin ?? v.motivoRespuesta ? ` · ${v.motivoFin ?? v.motivoRespuesta}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          ) : null}
        </>
      )}

      <PanelAsignacion vinculo={asignando} auxiliares={auxiliares} onCerrar={() => setAsignando(null)} />

      {renunciando ? (
        <ConfirmacionDestructiva
          abierto
          onCancelar={() => setRenunciando(null)}
          onConfirmar={() =>
            accion.mutate(
              { vinculo: renunciando.id, accion: 'renunciar', motivo: 'El despacho deja de llevar al cliente' },
              { onSuccess: () => setRenunciando(null) },
            )
          }
          titulo="Dejar al cliente"
          descripcion={
            <p>
              Nadie del despacho vuelve a entrar a <strong>{renunciando.razonSocial ?? renunciando.rncCliente}</strong>, y
              queda escrito en su auditoría. Para volver a llevarlo, el cliente tiene que aceptar otra invitación.
            </p>
          }
          textoAEscribir="dejar"
          etiquetaConfirmar="Dejar al cliente"
          trabajando={accion.isPending}
        />
      ) : null}
    </div>
  )
}

function Cliente({ vinculo }: { vinculo: VinculoDespacho }) {
  const presentacion = PRESENTACION_VINCULO[vinculo.estado]
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex items-center gap-2">
        <span className="truncate text-gris-900">{vinculo.razonSocial ?? `RNC ${formatearRNC(vinculo.rncCliente)}`}</span>
        <InsigniaEstado tono={presentacion.tono}>{presentacion.etiqueta}</InsigniaEstado>
      </span>
      <span className="numero text-etiqueta text-gris-600">
        RNC {formatearRNC(vinculo.rncCliente)}
        {vinculo.mensaje ? ` · «${vinculo.mensaje}»` : ''}
      </span>
    </span>
  )
}

function nombresAsignados(v: VinculoDespacho, equipo: MiembroDespacho[]): string {
  const correos = (v.asignados ?? [])
    .map((id) => equipo.find((m) => m.miembroId === id)?.email)
    .filter((c): c is string => typeof c === 'string')
  return correos.length === 0 ? '' : `Lo ven los titulares y ${correos.join(', ')}`
}

function Invitar() {
  const invitar = useInvitarCliente()
  const [rnc, setRnc] = useState('')
  const [mensaje, setMensaje] = useState('')
  const digitos = limpiarRNC(rnc)
  const estructura = digitos.length === 9 || digitos.length === 11

  const enviar = (evento: React.FormEvent): void => {
    evento.preventDefault()
    invitar.mutate(
      { rnc: digitos, mensaje: mensaje.trim() === '' ? undefined : mensaje.trim() },
      {
        onSuccess: () => {
          setRnc('')
          setMensaje('')
        },
      },
    )
  }

  return (
    <Tarjeta titulo="Invitar a un cliente">
      <form onSubmit={enviar} className="flex flex-col gap-3">
        <div className="grid gap-3 md:grid-cols-[220px_1fr]">
          <CampoTexto
            etiqueta="RNC o cédula del cliente"
            obligatorio
            numerico
            inputMode="numeric"
            value={rnc}
            onChange={(e) => setRnc(e.target.value)}
            error={rnc !== '' && !estructura ? 'Nueve dígitos el RNC, once la cédula.' : undefined}
          />
          <CampoTexto
            etiqueta="Mensaje para el cliente"
            anotacion="opcional"
            value={mensaje}
            maxLength={500}
            onChange={(e) => setMensaje(e.target.value)}
          />
        </div>
        {/* El dígito verificador avisa, no bloquea (regla 8). */}
        {estructura && digitoVerificadorCorrecto(digitos) === false ? (
          <p className="text-etiqueta text-ambar">El dígito verificador no cuadra. Puede estar bien igual: se envía.</p>
        ) : null}
        {invitar.isSuccess ? (
          <Banner tono={invitar.data.creada ? 'verde' : 'azul'} titulo={invitar.data.creada ? 'Invitación enviada' : 'Ya estaba'}>
            {invitar.data.mensaje}
          </Banner>
        ) : null}
        {invitar.isError ? <ErrorEnLinea error={invitar.error} accion="enviar la invitación" /> : null}
        <div>
          <Boton type="submit" variante="primario" icono={<Send />} disabled={!estructura || invitar.isPending}>
            {invitar.isPending ? 'Enviando…' : 'Enviar invitación'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  )
}

function PanelAsignacion({
  vinculo,
  auxiliares,
  onCerrar,
}: {
  vinculo: VinculoDespacho | null
  auxiliares: MiembroDespacho[]
  onCerrar: () => void
}) {
  const asignar = useAsignar()
  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(new Set())

  useEffect(() => {
    setElegidos(new Set(vinculo?.asignados ?? []))
    asignar.reset()
    // Solo al abrir el panel con otro cliente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinculo])

  const alternar = (id: string): void =>
    setElegidos((previos) => {
      const siguiente = new Set(previos)
      if (siguiente.has(id)) siguiente.delete(id)
      else siguiente.add(id)
      return siguiente
    })

  return (
    <PanelLateral
      abierto={vinculo !== null}
      onCerrar={onCerrar}
      titulo="Quién del equipo lo ve"
      subtitulo={vinculo?.razonSocial ?? undefined}
      pie={
        <>
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            disabled={vinculo === null || asignar.isPending}
            onClick={() =>
              vinculo && asignar.mutate({ vinculo: vinculo.id, miembros: [...elegidos] }, { onSuccess: onCerrar })
            }
          >
            {asignar.isPending ? 'Guardando…' : 'Guardar'}
          </Boton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-cuerpo text-gris-600">
          Los titulares ven toda la cartera. Un auxiliar ve solo los clientes que se le marquen aquí, y deja de verlos en
          cuanto se le desmarcan. El cambio queda en la auditoría del cliente.
        </p>
        <ul className="flex flex-col gap-2">
          {auxiliares.map((m) => (
            <li key={m.miembroId}>
              <label className="flex items-center gap-2 text-cuerpo text-gris-900">
                <input type="checkbox" checked={elegidos.has(m.miembroId)} onChange={() => alternar(m.miembroId)} />
                {m.email}
              </label>
            </li>
          ))}
        </ul>
        {asignar.isError ? <ErrorEnLinea error={asignar.error} accion="guardar quién lo ve" /> : null}
      </div>
    </PanelLateral>
  )
}
